import { usePortalMessage } from "../../i18n/message.js";
import { portalLocale, portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `/join#token=…` — joining a workspace from an invitation link (design §16.37).
 *
 * The token is in the FRAGMENT, as the shop screen's counter key is: a fragment is never sent to a
 * server, so the link stays out of every access log and proxy on the way. The page reads it, asks
 * the API what it joins (`inspectInvitation`), and then offers exactly one of four things:
 *
 *   · signed in as the invited address → one button, "Join <workspace>";
 *   · signed in as SOMEONE ELSE → says so before any click, with "Sign out and continue"
 *     (customer-portal review 2026-09-25 #157: the page knew the address, offered "Join as QA
 *     Viewer", and only said "signed in as someone else" after the click, with no way to sign out);
 *   · not signed in, and the address already has a WeTalk sign-in → its password, then join
 *     (review #143: it offered to SET a password and failed only after it was typed twice);
 *   · not signed in, a new address → name, password, confirm → `acceptInvitation` creates the
 *     person for the invitation's own address (shown, not editable) and signs them in.
 *
 * Someone who is already in the workspace is told so in words (review #144, once a 500); any
 * failure of WeTalk's own shows its request id. Either way a join lands on the dashboard, scoped
 * to the workspace joined.
 *
 * AMENDED 2026-10-12 (ADR 0056, portal amendment): a way-in page, so it is drawn in the public design's frame and card
 * (`./public-frame.tsx`) like Log in and Sign up; what it asks and when is unchanged.
 *
 * AMENDED 2026-11-03 (owner decision "Both cases"; design §16.37 amendment) — AN EMAILED CODE, beside the password,
 * only while the API says a code can be sent (`readSignInCodeAvailability`, as Log in asks); otherwise the page is as
 * above. The code always goes to the INVITATION's address (`requestInvitationSignInCode` with the token — the page never
 * sends an address):
 *   · the address already signs in → "Email me a sign-in code instead" → the code → `verifySignInCode`, then — as after
 *     its password — the person's authenticator when theirs asks for one (an emailed code is never the second step,
 *     ADR 0037), then `acceptInvitation` as that person, which judges the organization's sign-in policy (an
 *     authenticator required, single sign-on only, allowed networks) exactly as after Log in;
 *   · a new address → "Join with an emailed code instead" → name and the code → `acceptInvitation({ code })`: the API
 *     creates the person with NO password (they can add one later in My security) only with the code mailed to the
 *     invitation's own address, in this browser. An organization that requires an authenticator still asks for one.
 * The password path gained the same second step: an authenticator was refused on Join with no way to answer it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ApiError } from "@wetalk/api-client";
import { Button, Field, Input } from "@wetalk/ui";

import "./auth.css";
import { useState, type FormEvent, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { apiProblemText } from "../../api/api-error.js";
import { AuthSessionUnscopedError, type AuthSessionView } from "../../api/gateway.js";
import type { SecondStepFactor } from "../../api/slice/personal-security.js";

import { useAdoptAuthSession } from "./adopt-auth-session.js";
import { GoogleMark, MicrosoftMark, PublicFrame, PublicKicker } from "./public-frame.js";

// AMENDED 2026-10-06 (plan-014 B2, A4's note): an invitation may grant any of the seven workspace roles.
const ROLE_WORD = {
  get owner() { return portalText("core", "auth.an_owner_d8d838"); },
  get admin() { return portalText("core", "auth.an_admin_9df3ef"); },
  get member() { return portalText("core", "auth.a_member_2a7062"); },
  get viewer() { return portalText("core", "auth.a_viewer_ad521b"); },
  get developer() { return portalText("core", "auth.a_developer_8aecdf"); },
  get campaign_manager() { return portalText("core", "auth.a_campaign_manager_0ce110"); },
  get billing() { return portalText("core", "auth.a_billing_member_9bc4b9"); },
} as const;
const MINIMUM_PASSWORD = 10;
const CURRENT_AUTH_SESSION_KEY = ["join", "current-session"] as const;

/**
 * plan-028 (OD-11): joining with Google or Microsoft leaves this page for the provider and comes back to `/join` with no
 * fragment, so the token is kept for that round trip in this tab's sessionStorage — never in a URL, never sent to the
 * server except in `beginProviderSignIn`'s body — and dropped once the person has joined.
 */
const PROVIDER_JOIN_TOKEN_KEY = "wetalk.join.provider_token";

function storedJoinToken(): string | null {
  try {
    const held = window.sessionStorage.getItem(PROVIDER_JOIN_TOKEN_KEY);
    return held === null || held === "" ? null : held;
  } catch {
    return null;
  }
}

function forgetJoinToken(): void {
  try {
    window.sessionStorage.removeItem(PROVIDER_JOIN_TOKEN_KEY);
  } catch {
    // Storage blocked: nothing was kept.
  }
}

/** The token from `#token=…`, or the one kept for a provider's round trip, or `null`. */
function tokenOfLocation(): string | null {
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token = fragment.get("token");
  return token === null || token === "" ? storedJoinToken() : token;
}

export function JoinScreen(): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const adoptAuthSession = useAdoptAuthSession();
  const [token] = useState<string | null>(tokenOfLocation);
  // plan-028 (OD-11): which provider buttons work, and the start that keeps the invitation token out of every URL.
  const providers = useQuery({ queryKey: ["auth", "sign-in-providers"], queryFn: () => gateway.readSignInProviders(), staleTime: 60_000, retry: false });
  const startProvider = useMutation({
    mutationFn: async (provider: "google" | "microsoft") => {
      if (token === null) throw new Error("no invitation token");
      try {
        window.sessionStorage.setItem(PROVIDER_JOIN_TOKEN_KEY, token);
      } catch {
        // Storage blocked: the person opens the invitation link again after signing in.
      }
      return gateway.beginProviderSignIn(provider, { returnPath: "/join", invitationToken: token });
    },
    onSuccess: (started) => {
      window.location.assign(started.startUrl);
    },
  });
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [problem, setProblem] = usePortalMessage();
  const [alreadyIn, setAlreadyIn] = useState(false);
  /** 2026-11-03: the emailed code — null until "Email me a code" was pressed and the API said it is on its way. */
  const [codeSent, setCodeSent] = useState<{ readonly expiresAt: string } | null>(null);
  const [code, setCode] = useState("");
  /** 2026-11-03: set once a sign-in on this page still owes the person's authenticator (plan-014 C2). */
  const [secondStep, setSecondStep] = useState(false);
  const [secondStepFactor, setSecondStepFactor] = useState<SecondStepFactor>("totp");

  const invitation = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => gateway.inspectInvitation(token ?? ""),
    enabled: token !== null,
    retry: false,
  });
  const authSession = useQuery({
    queryKey: CURRENT_AUTH_SESSION_KEY,
    queryFn: () => gateway.currentAuthSession(),
    retry: false,
  });

  // 2026-11-03: whether an emailed code can be sent at all — asked only of a browser with no sign-in. A failed read offers
  // nothing rather than a control that cannot work (as Log in).
  const codeAvailability = useQuery({
    queryKey: ["personal-security", "sign-in-code-availability"],
    queryFn: () => gateway.readSignInCodeAvailability(),
    enabled: token !== null && authSession.data === null,
    staleTime: 300_000,
    retry: false,
  });
  const codeAvailable = codeAvailability.data === true;

  const joined = async (opened: AuthSessionView): Promise<void> => {
    // The fragment carried a single-use secret: drop it from history before moving on.
    window.history.replaceState(null, "", "/join");
    forgetJoinToken();
    // CL #104 (review row #104): the shell's guard must read the auth_session just opened, never
    // a cached "nobody".
    adoptAuthSession(opened);
    await navigate({ to: "/dashboard" });
  };

  const refused = (raised: unknown): void => {
    // 409 from accept: this person is already in the workspace. Not an error to retry.
    setAlreadyIn(raised instanceof ApiError && raised.status === 409);
    setProblem(() => (apiProblemText(raised, portalText("core", "auth.the_invitation_could_not_be_accepted_50fd96"))));
  };

  const join = useMutation({
    mutationFn: (asNewPerson: boolean) =>
      gateway.acceptInvitation({
        token: token ?? "",
        displayName: asNewPerson ? displayName.trim() : null,
        password: asNewPerson ? password : null,
        code: null,
      }),
    onSuccess: joined,
    onError: refused,
  });

  /** Joins as the person this browser just signed in (by password or code). */
  const acceptAsSignedIn = (): Promise<AuthSessionView> =>
    gateway.acceptInvitation({ token: token ?? "", displayName: null, password: null, code: null });

  /**
   * After a sign-in on this page: the person's own authenticator first when it asks (an emailed code and a password
   * are each ONE factor), then the join. `"second_step"` when the page must ask for it.
   */
  const afterSignIn = async (signIn: () => Promise<AuthSessionView>): Promise<AuthSessionView | "second_step"> => {
    try {
      await signIn();
    } catch (raised) {
      // In no organization yet, or in several: the sign-in is open all the same, and joining is this page's next step.
      if (!(raised instanceof AuthSessionUnscopedError)) throw raised;
    }
    await queryClient.invalidateQueries({ queryKey: CURRENT_AUTH_SESSION_KEY });
    const methods = await gateway.readMySignInMethods();
    if (methods.secondStepEnforced && !methods.secondStepVerified) return "second_step";
    return acceptAsSignedIn();
  };

  const afterSignInDone = async (next: AuthSessionView | "second_step"): Promise<void> => {
    if (next === "second_step") {
      setCode("");
      setProblem(null);
      setSecondStep(true);
      return;
    }
    await joined(next);
  };

  /** An address that already signs in: its password first, then the join as that person. */
  const logInAndJoin = useMutation({
    mutationFn: (emailAddress: string) => afterSignIn(() => gateway.logIn({ emailAddress, password })),
    onSuccess: afterSignInDone,
    onError: refused,
  });

  /** 2026-11-03: a code to the invitation's own address — the API reads the address from the token. */
  const askForCode = useMutation({
    mutationFn: () => gateway.requestInvitationSignInCode(token ?? ""),
    onSuccess: (asked) => {
      setProblem(null);
      setCode("");
      setCodeSent(asked);
    },
    onError: refused,
  });

  /** 2026-11-03: an address that already signs in, with its emailed code — then as after its password. */
  const codeSignInAndJoin = useMutation({
    mutationFn: () => afterSignIn(() => gateway.verifySignInCode(code.replace(/\s/g, ""))),
    onSuccess: afterSignInDone,
    onError: refused,
  });

  /** 2026-11-03: a new address joins with its name and the emailed code — no password. */
  const codeJoin = useMutation({
    mutationFn: () =>
      gateway.acceptInvitation({ token: token ?? "", displayName: displayName.trim(), password: null, code: code.replace(/\s/g, "") }),
    onSuccess: joined,
    onError: refused,
  });

  /** The authenticator (or a recovery code) a sign-in on this page still owes, then the join. */
  const answerSecondStep = useMutation({
    mutationFn: async () => {
      await gateway.answerMfaChallenge(secondStepFactor, code.replace(/\s/g, ""));
      return acceptAsSignedIn();
    },
    onSuccess: async (opened) => {
      setSecondStep(false);
      await joined(opened);
    },
    onError: (raised: unknown) => {
      if (raised instanceof ApiError && raised.code === "mfa_factor_invalid") {
        setProblem(
          () => (secondStepFactor === "totp"
            ? portalText("core", "auth.that_code_is_not_right_enter_the_6_digits_your_app_shows_now_5af97e")
            : portalText("core", "auth.that_recovery_code_is_not_right_or_it_was_used_already_916ef9")),
        );
        return;
      }
      setSecondStep(false);
      refused(raised);
    },
  });

  const onCode = (event: FormEvent, asNewPerson: boolean): void => {
    event.preventDefault();
    setProblem(null);
    if (asNewPerson && displayName.trim() === "") {
      setProblem(() => (portalText("core", "auth.type_your_name_as_the_people_you_work_with_will_see_it_7ae3a3")));
      return;
    }
    if (code.trim() === "") {
      setProblem(() => (portalText("core", "auth.enter_the_6_digit_code_from_the_email_029180")));
      return;
    }
    if (asNewPerson) codeJoin.mutate();
    else codeSignInAndJoin.mutate();
  };

  const signOut = useMutation({
    mutationFn: () => gateway.logOut(),
    onSuccess: async () => {
      setProblem(null);
      await queryClient.invalidateQueries({ queryKey: CURRENT_AUTH_SESSION_KEY });
    },
    onError: (raised: unknown) => {
      setProblem(() => (apiProblemText(raised, portalText("core", "auth.you_could_not_be_signed_out_try_again_b755a0"))));
    },
  });

  const onNewPerson = (event: FormEvent): void => {
    event.preventDefault();
    setProblem(null);
    if (displayName.trim() === "") {
      setProblem(() => (portalText("core", "auth.type_your_name_as_the_people_you_work_with_will_see_it_7ae3a3")));
      return;
    }
    if (password.length < MINIMUM_PASSWORD) {
      setProblem(() => (portalText("core", "auth.the_password_needs_at_least_characters_8f32d1", { value0: String(MINIMUM_PASSWORD) })));
      return;
    }
    if (password !== confirmation) {
      setProblem(() => (portalText("core", "auth.the_two_passwords_are_not_the_same_83838b")));
      return;
    }
    join.mutate(true);
  };

  const signedIn = authSession.data ?? null;
  const preview = invitation.data;
  const mismatch =
    signedIn !== null &&
    preview !== undefined &&
    signedIn.person.emailAddress.toLowerCase() !== preview.emailAddress.toLowerCase();
  // Only a new address is asked to SET a password; everyone else just joins.
  const setsPassword = preview !== undefined && signedIn === null && !preview.signInExists;

  const problemLine =
    problem === null ? null : (
      <p className="wt-field-error" role="alert">
        {problem}
        {alreadyIn ? (
          <>
            {" "}
            <Link to="/dashboard">{portalText("core", "auth.open_the_console_e4afd6")}</Link>
          </>
        ) : null}
      </p>
    );

  return (
    <PublicFrame>
      <div className="pd-form">
        <div className="pd-form">
          <PublicKicker>{portalText("core", "auth.entry.welcome")}</PublicKicker>
          <h1>
            {preview === undefined
              ? portalText("core", "auth.join_your_team_244923")
              : portalText("core", "auth.join_as_05273e", { value0: preview.accountName, value1: ROLE_WORD[preview.role] })}
          </h1>
          <p className="pd-lede">
            {preview === undefined ? (
              <>{portalText("core", "auth.open_the_invitation_link_you_were_sent_to_join_a_workspace_on_wet_06cf91")}</>
            ) : (
              <>{portalText("core", "auth.you_were_invited_to_the_6fe726")}{preview.workspaceName}{portalText("core", "auth.workspace_0f4b4f")}{setsPassword ? (codeSent === null ? portalText("core", "auth._set_a_password_to_finish_a4a0d8") : portalText("core", "auth.join_code.lede_finish")) : ""}
              </>
            )}
          </p>
        </div>

        {token !== null && signedIn === null && invitation.data !== undefined && (providers.data?.google === true || providers.data?.microsoft === true) ? (
          <div className="pd-providers">
            {(["google", "microsoft"] as const)
              .filter((provider) => providers.data?.[provider] === true)
              .map((provider) => (
                <button
                  key={provider}
                  type="button"
                  className="pd-provider"
                  disabled={startProvider.isPending}
                  onClick={() => {
                    startProvider.mutate(provider);
                  }}
                >
                  {provider === "google" ? <GoogleMark /> : <MicrosoftMark />}
                  {portalText("core", provider === "google" ? "auth.entry.google" : "auth.entry.microsoft")}
                </button>
              ))}
          </div>
        ) : null}

        {token === null ? (
          <div className="c-entry-form">
            <p className="wt-field-error" role="alert">{portalText("core", "auth.this_page_needs_the_full_invitation_link_open_it_again_from_the_m_6bb3d0")}</p>
          </div>
        ) : invitation.isPending ? (
          <div className="c-entry-form">
            <p className="c-note">{portalText("core", "auth.opening_the_invitation_79c88d")}</p>
          </div>
        ) : invitation.isError ? (
          <div className="c-entry-form">
            <p className="wt-field-error" role="alert">
              {apiProblemText(invitation.error, portalText("core", "auth.this_invitation_link_cannot_be_used_fe99b7"))}
            </p>
            <Link to="/log-in">{portalText("core", "auth.log_in_f7c400")}</Link>
          </div>
        ) : secondStep ? (
          <form
            className="c-entry-form"
            aria-labelledby="join-second-step-title"
            onSubmit={(event) => {
              event.preventDefault();
              setProblem(null);
              if (code.trim() === "") {
                setProblem(() => (secondStepFactor === "totp" ? portalText("core", "auth.enter_the_6_digit_code_from_your_authenticator_app_ee11fc") : portalText("core", "auth.enter_one_of_your_recovery_codes_44618f")));
                return;
              }
              answerSecondStep.mutate();
            }}
          >
            <h2 id="join-second-step-title">{portalText("core", "auth.your_second_step_32986d")}</h2>
            <p className="c-note">
              {secondStepFactor === "totp"
                ? portalText("core", "auth.enter_the_code_your_authenticator_app_shows_for_wetalk_a69c9b")
                : portalText("core", "auth.enter_one_of_the_recovery_codes_you_saved_each_works_once_4d821d")}
            </p>
            <Field label={secondStepFactor === "totp" ? portalText("core", "auth.6_digit_code_0c52ea") : portalText("core", "auth.recovery_code_3cb5fd")} {...(problem === null ? {} : { error: problem })}>
              {(control) => (
                <Input
                  {...control}
                  name="secondStepCode"
                  inputMode={secondStepFactor === "totp" ? "numeric" : undefined}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => {
                    setCode(event.currentTarget.value);
                  }}
                />
              )}
            </Field>
            <Button variant="primary" type="submit" block disabled={answerSecondStep.isPending}>
              {answerSecondStep.isPending ? portalText("core", "auth.joining_f44c1e") : portalText("core", "auth.join_3ebf99", { value0: invitation.data.workspaceName })}
            </Button>
            <Button
              variant="ghost"
              block
              onClick={() => {
                setSecondStepFactor(secondStepFactor === "totp" ? "recovery_code" : "totp");
                setCode("");
                setProblem(null);
              }}
            >
              {secondStepFactor === "totp" ? portalText("core", "auth.use_a_recovery_code_instead_104df4") : portalText("core", "auth.use_my_authenticator_app_instead_e5d89a")}
            </Button>
          </form>
        ) : mismatch ? (
          <div className="c-entry-form">
            <p className="c-note" role="alert">{portalText("core", "auth.this_invitation_is_for_be3107")}{" "}{invitation.data.emailAddress}{portalText("core", "auth._but_you_are_signed_in_as_f0d791")}{" "}
              {signedIn.person.emailAddress}{portalText("core", "auth._sign_out_to_join_with_the_invited_address_6e6b31")}</p>
            {problemLine}
            <Button
              variant="primary"
              block
              disabled={signOut.isPending}
              onClick={() => {
                signOut.mutate();
              }}
            >
              {signOut.isPending ? portalText("core", "auth.signing_out_3ca85c") : portalText("core", "auth.sign_out_and_continue_784c94")}
            </Button>
          </div>
        ) : signedIn !== null ? (
          <div className="c-entry-form">
            <div className="c-entry-identifier">
              <span className="c-entry-identifier-address">{invitation.data.emailAddress}</span>
            </div>
            {problemLine}
            <Button
              variant="primary"
              block
              disabled={join.isPending || alreadyIn}
              onClick={() => {
                setProblem(null);
                join.mutate(false);
              }}
            >
              {join.isPending ? portalText("core", "auth.joining_f44c1e") : portalText("core", "auth.join_3ebf99", { value0: invitation.data.workspaceName })}
            </Button>
          </div>
        ) : codeSent !== null ? (
          <form
            className="c-entry-form"
            onSubmit={(event) => {
              onCode(event, !invitation.data.signInExists);
            }}
          >
            <div className="c-entry-identifier">
              <span className="c-entry-identifier-address">{invitation.data.emailAddress}</span>
            </div>
            <p className="c-note">
              {portalText("core", "auth.join_code.on_its_way")}{" "}
              {new Date(codeSent.expiresAt).toLocaleTimeString(portalLocale() === "en" ? "en-GB" : "el-GR", { hour: "2-digit", minute: "2-digit" })}.
            </p>
            {invitation.data.signInExists ? null : (
              <Field label={portalText("core", "auth.your_name_ab4229")}>
                {(control) => (
                  <Input
                    {...control}
                    name="displayName"
                    value={displayName}
                    autoComplete="name"
                    onChange={(event) => {
                      setDisplayName(event.currentTarget.value);
                    }}
                  />
                )}
              </Field>
            )}
            <Field label={portalText("core", "auth.sign_in_code_6af151")} {...(problem === null ? {} : { error: problem })}>
              {(control) => (
                <Input
                  {...control}
                  name="signInCode"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => {
                    setCode(event.currentTarget.value);
                  }}
                />
              )}
            </Field>
            {invitation.data.signInExists ? null : <p className="c-note">{portalText("core", "auth.join_code.password_later")}</p>}
            <Button
              variant="primary"
              type="submit"
              block
              disabled={codeJoin.isPending || codeSignInAndJoin.isPending || alreadyIn}
            >
              {codeJoin.isPending || codeSignInAndJoin.isPending
                ? portalText("core", "auth.joining_f44c1e")
                : invitation.data.signInExists
                  ? portalText("core", "auth.log_in_and_join_25b417")
                  : portalText("core", "auth.join_3ebf99", { value0: invitation.data.workspaceName })}
            </Button>
            <Button variant="secondary" block disabled={askForCode.isPending} onClick={() => askForCode.mutate()}>{portalText("core", "auth.send_a_new_code_91904a")}</Button>
            <Button
              variant="ghost"
              block
              onClick={() => {
                setCodeSent(null);
                setCode("");
                setProblem(null);
              }}
            >
              {invitation.data.signInExists ? portalText("core", "auth.use_my_password_instead_b5501c") : portalText("core", "auth.join_code.set_password_instead")}
            </Button>
          </form>
        ) : invitation.data.signInExists ? (
          <form
            className="c-entry-form"
            onSubmit={(event) => {
              event.preventDefault();
              setProblem(null);
              logInAndJoin.mutate(invitation.data.emailAddress);
            }}
          >
            <div className="c-entry-identifier">
              <span className="c-entry-identifier-address">{invitation.data.emailAddress}</span>
            </div>
            <p className="c-note">{portalText("core", "auth.this_address_already_has_a_wetalk_sign_in_type_its_password_to_jo_8e5af0")}</p>
            <Field label={portalText("core", "auth.password_8be3c9")} {...(problem === null ? {} : { error: problem })}>
              {(control) => (
                <Input
                  {...control}
                  name="password"
                  type="password"
                  value={password}
                  autoComplete="current-password"
                  onChange={(event) => {
                    setPassword(event.currentTarget.value);
                  }}
                />
              )}
            </Field>
            <Button
              variant="primary"
              type="submit"
              block
              disabled={logInAndJoin.isPending || password === "" || alreadyIn}
            >
              {logInAndJoin.isPending ? portalText("core", "auth.joining_f44c1e") : portalText("core", "auth.log_in_and_join_25b417")}
            </Button>
            {codeAvailable ? (
              <Button variant="ghost" block disabled={askForCode.isPending} onClick={() => askForCode.mutate()}>
                {portalText("core", "auth.email_me_a_sign_in_code_instead_d078c2")}
              </Button>
            ) : null}
          </form>
        ) : (
          <form className="c-entry-form" onSubmit={onNewPerson}>
            <div className="c-entry-identifier">
              <span className="c-entry-identifier-address">{invitation.data.emailAddress}</span>
            </div>

            <Field label={portalText("core", "auth.your_name_ab4229")}>
              {(control) => (
                <Input
                  {...control}
                  name="displayName"
                  value={displayName}
                  autoComplete="name"
                  onChange={(event) => {
                    setDisplayName(event.currentTarget.value);
                  }}
                />
              )}
            </Field>

            <Field label={portalText("core", "auth.password_8be3c9")} description={portalText("core", "auth.at_least_characters_ff975e", { value0: String(MINIMUM_PASSWORD) })}>
              {(control) => (
                <Input
                  {...control}
                  name="password"
                  type="password"
                  value={password}
                  autoComplete="new-password"
                  onChange={(event) => {
                    setPassword(event.currentTarget.value);
                  }}
                />
              )}
            </Field>

            <Field label={portalText("core", "auth.password_again_84296b")} {...(problem === null ? {} : { error: problem })}>
              {(control) => (
                <Input
                  {...control}
                  name="passwordConfirmation"
                  type="password"
                  value={confirmation}
                  autoComplete="new-password"
                  onChange={(event) => {
                    setConfirmation(event.currentTarget.value);
                  }}
                />
              )}
            </Field>

            <Button variant="primary" type="submit" block disabled={join.isPending}>
              {join.isPending ? portalText("core", "auth.joining_f44c1e") : portalText("core", "auth.set_password_and_join_d0f1ad")}
            </Button>
            {codeAvailable ? (
              <Button variant="ghost" block disabled={askForCode.isPending} onClick={() => askForCode.mutate()}>
                {portalText("core", "auth.join_code.join_with_code_instead")}
              </Button>
            ) : null}
          </form>
        )}
      </div>
    </PublicFrame>
  );
}
